import { createApp } from 'vue'
import { useAuthenticationStore } from '@/stores/authentication'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import 'vuetify/dist/vuetify.min.css'
import * as Sentry from '@sentry/vue'

import { useApi } from '@/api/useApi'
import { pinia } from '@/modules/pinia'
import apiUrls from '@/api/apiUrls'

import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import { AUTH_STORE_KEY } from './constants'
import { OpengroundPlugin, setSpeckleConfig } from '@haskoning/vue-speckle'
import { opengroundAdapter } from './adapters/openground'

const speckleConfig = {
  serverUrl: import.meta.env.VITE_APP_SERVER_URL,
  appId: import.meta.env.VITE_APP_SPECKLE_ID,
  appSecret: import.meta.env.VITE_APP_SPECKLE_SECRET,
  appName: import.meta.env.VITE_APP_SPECKLE_NAME,
  workspaceId: import.meta.env.VITE_APP_SPECKLE_WORKSPACE_ID
}
setSpeckleConfig(
  speckleConfig.serverUrl,
  speckleConfig.workspaceId,
  speckleConfig.appId,
  speckleConfig.appSecret,
  speckleConfig.appName
)

pinia.use(({ store }) => {
  store.$api = api
  store.$apiUrls = apiUrls
})

const app = createApp(App).use(vuetify).use(pinia)
const { api } = useApi()
const authStore = useAuthenticationStore()
const { bus } = useEventsBus()
const mode = import.meta.env.MODE
if (mode !== 'local-development') {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracePropagationTargets: ['*'],
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        triggerLabel: 'Feedback',
        formTitle: 'Send Feedback',
        submitButtonLabel: 'Send Feedback',
        cancelButtonLabel: 'Cancel',
        messagePlaceholder: 'What would you like to tell us?',
        successMessageText: 'Thank you for your feedback!',
        colorScheme: 'light',
        showName: true,
        showEmail: true,
        enableScreenshot: true,
        autoInject: false
      })
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}

app.use(ComponentLibrary, { externalBus: bus })
app
  .use(OpengroundPlugin, {
    router,
    callbackURL: import.meta.env.VITE_APP_OPENGROUND_CALLBACK_URL,
    clientId: import.meta.env.VITE_APP_OPENGROUND_CLIENT_ID,
    backendAdapter: opengroundAdapter
  })
  .provide(AUTH_STORE_KEY, authStore)
  .use(router)

router.isReady().then(() => {
  const feedback = Sentry.getFeedback()
  if (!feedback) {
    return
  }

  let widget: ReturnType<typeof feedback.createWidget> | null = null

  const isAuthenticated = () => {
    return authStore.user !== null
  }

  const updateFeedbackWidget = () => {
    if (isAuthenticated() && !widget) {
      widget = feedback.createWidget()
      widget.appendToDom()
      if (authStore.user?.email) {
        Sentry.setUser({
          username: authStore.user.first_name ?? '',
          email: authStore.user.email ?? ''
        })
      }
    } else if (!isAuthenticated() && widget) {
      widget.removeFromDom()
      widget = null
      Sentry.setUser(null)
    }
  }
  updateFeedbackWidget()
  authStore.$subscribe(updateFeedbackWidget)
})

app.mount('#app')
